// Appearance: System (follow the computer's light/dark setting), Light or Dark. The choice is per browser
// (localStorage), like the iPhone's own setting is per device. <html data-theme="light|dark"> always carries the
// resolved theme: tokens.css, Tailwind's `dark:` variant and CSS `light-dark()` (through `color-scheme`) all read it.
//
// public/theme-init.js applies the stored choice before the first paint (a plain script in <head>, so the page never
// flashes the wrong theme); this module keeps it current afterwards: when the choice changes here or in another tab,
// and when the system setting flips while the choice is System. Keep the storage key and the rules in both in step.

import { useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'gt.theme';
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/** A stored value as a preference; anything unknown (or nothing stored) means System. */
export function parseThemePreference(raw: string | null | undefined): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

// Storage can be missing or throw (private windows, blocked site data); the choice then lasts for this page only.
let memoryPreference: ThemePreference = 'system';

function readStored(): ThemePreference {
  try {
    return parseThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return memoryPreference;
  }
}

function writeStored(preference: ThemePreference): void {
  memoryPreference = preference;
  try {
    if (preference === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Kept in memory only.
  }
}

// One list for the page, held here so its change listener lives as long as the page does.
let media: MediaQueryList | null | undefined;

function systemMedia(): MediaQueryList | null {
  if (media === undefined) {
    media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(SYSTEM_DARK_QUERY) : null;
  }
  return media;
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
}

const SERVER_STATE: ThemeState = { preference: 'system', resolved: 'light' };
let state: ThemeState | null = null;
const listeners = new Set<() => void>();

function compute(preference: ThemePreference): ThemeState {
  return { preference, resolved: resolveTheme(preference, systemMedia()?.matches ?? false) };
}

/**
 * Swaps the theme in one step. Fields and buttons have color transitions, so without this each one would fade over
 * at its own pace; transitions are off until the new colors have been computed.
 */
function setRootTheme(resolved: ResolvedTheme): void {
  const freeze = document.createElement('style');
  freeze.textContent = '*,*::before,*::after{transition:none!important}';
  document.head.appendChild(freeze);
  document.documentElement.dataset.theme = resolved;
  void window.getComputedStyle(document.body).color;
  window.setTimeout(() => freeze.remove(), 1);
}

/** Puts the resolved theme on <html> and tells subscribers, if anything changed. */
function update(next: ThemeState): void {
  if (document.documentElement.dataset.theme !== next.resolved) setRootTheme(next.resolved);
  if (state && state.preference === next.preference && state.resolved === next.resolved) return;
  state = next;
  for (const notify of listeners) notify();
}

let started = false;

/**
 * Applies the stored choice and starts following the system setting and other tabs, once per page. main.tsx calls it
 * before the first render: no page may depend on a theme control being on screen (on a phone the rail with the
 * account menu is mounted only while its drawer is open). It also sets data-theme if theme-init.js didn't load.
 */
export function initTheme(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  update(compute(readStored()));
  systemMedia()?.addEventListener('change', () => {
    if (state?.preference === 'system') update(compute('system'));
  });
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_STORAGE_KEY || event.key === null) update(compute(readStored()));
  });
}

function getState(): ThemeState {
  initTheme();
  return state ?? SERVER_STATE;
}

function subscribe(notify: () => void): () => void {
  initTheme();
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

export function setThemePreference(preference: ThemePreference): void {
  initTheme();
  writeStored(preference);
  update(compute(preference));
}

/** The chosen appearance, the theme it resolves to right now, and a setter. */
export function useTheme(): ThemeState & { setPreference: (preference: ThemePreference) => void } {
  const current = useSyncExternalStore(subscribe, getState, () => SERVER_STATE);
  return { ...current, setPreference: setThemePreference };
}

/** The theme in effect ('light' or 'dark'), for code that can't use CSS (map styles, canvas colors). */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, () => getState().resolved, () => SERVER_STATE.resolved);
}
